import { logger } from '@/utils/logger';

import { AllowedConnectorTypes } from '../config/allowed-connector-types';
import { AllowedReadFormatTypes } from '../config/allowed-format-types';
import { AllowedWriteFormatTypes } from '../config/allowed-format-types';
import { AllowedImportTypes } from '../config/allowed-import-types';
import { AllowedStatementTypes } from '../config/allowed-statement-types';
import { AllowedTaskFunctions, AllowedTaskTypes } from '../config/allowed-task-types';
import { preSaveTransformers } from '../transformers/preSaveTransformers';

export const updateRecipeFromNodesAndEdges = (modifiedNodes, modifiedEdges, originalRecipe) => {
  // Clone the original recipe to avoid mutating it directly
  const updatedRecipe = JSON.parse(JSON.stringify(originalRecipe));
  // Initialize recipe structure if not exists
  updatedRecipe.connectors = updatedRecipe.connectors || {};
  updatedRecipe.readFormats = updatedRecipe.readFormats || {};
  updatedRecipe.writeFormats = updatedRecipe.writeFormats || {};
  updatedRecipe.tasks = updatedRecipe.tasks || {};
  updatedRecipe.statements = updatedRecipe.statements || {};
  updatedRecipe.imports = updatedRecipe.imports || {};

  // Update and add new connectors (excluding localFileImporter which goes to imports)
  const connectors =
    modifiedNodes
      ?.filter(
        (node) => AllowedConnectorTypes?.includes(node?.type) && node?.type !== 'localFileImporter'
      )
      ?.map((node) => {
        const existingConnector = updatedRecipe?.connectors?.connector?.find(
          (conn) => conn?.['@id'] === node?.id
        );
        return {
          ...(existingConnector || {}),
          ...node?.data,
          '@id': node?.id,
        };
      }) || [];

  if (connectors.length > 0) {
    updatedRecipe.connectors = { connector: connectors };
  } else {
    // Explicitly remove the connectors section if there are no connectors
    delete updatedRecipe.connectors;
  }

  // Update and add new statements
  const statements =
    modifiedNodes
      ?.filter((node) => {
        // as we have command nodes suffixed with "Command", we need to trim that and then check
        return AllowedStatementTypes?.includes(node?.type?.replace('Command', ''));
      })
      ?.map((node) => {
        const existingStatement = updatedRecipe?.statements?.statement?.find(
          (stmt) => stmt?.['@id'] === node?.id
        );
        return {
          ...(existingStatement || {}),
          ...node?.data,
          '@id': node?.id,
        };
      }) || [];
  if (statements.length > 0) {
    updatedRecipe.statements = { statement: statements };
  } else {
    // Explicitly remove the statements section if there are no statements
    delete updatedRecipe.statements;
  }

  // Update and add new read formats
  const readFormats =
    modifiedNodes
      ?.filter(
        (node) =>
          AllowedReadFormatTypes?.includes(node?.type) && !AllowedImportTypes?.includes(node?.type)
      )
      ?.map((node) => {
        const existingReadFormat = updatedRecipe?.readFormats?.readFormat?.find(
          (format) => format?.['@id'] === node?.id
        );
        return {
          ...(existingReadFormat || {}),
          ...node?.data,
          '@id': node?.id,
          type: node?.type?.replace('Reader', ''),
        };
      }) || [];
  if (readFormats.length > 0) {
    updatedRecipe.readFormats.readFormat = readFormats;
  } else {
    delete updatedRecipe.readFormats;
  }

  // Update and add new write formats
  const writeFormats =
    modifiedNodes
      ?.filter(
        (node) =>
          AllowedWriteFormatTypes?.includes(node?.type) && !AllowedImportTypes?.includes(node?.type)
      )
      ?.map((node) => {
        const existingWriteFormat = updatedRecipe?.writeFormats?.writeFormat?.find(
          (format) => format?.['@id'] === node?.id
        );
        return {
          ...(existingWriteFormat || {}),
          ...node?.data,
          '@id': node?.id,
          type: node?.type?.replace('Writer', ''),
        };
      }) || [];
  if (writeFormats.length > 0) {
    updatedRecipe.writeFormats.writeFormat = writeFormats;
  } else {
    delete updatedRecipe.writeFormats;
  }

  // Update and add new tasks
  /**
   * notes:
   * when we are generating the recipe trim the function name from the type
   */
  const tasks =
    modifiedNodes
      ?.filter((node) => AllowedTaskTypes?.includes(node?.type))
      ?.map((node) => {
        const existingTask = updatedRecipe?.tasks?.task?.find((task) => task?.['@id'] === node?.id);
        let type = '';
        AllowedTaskFunctions?.forEach((fnName) => {
          if (node?.type?.includes(fnName)) {
            type = node?.type?.replace(fnName, '');
          }
        });
        return {
          ...(existingTask || {}),
          ...node?.data,
          '@id': node?.id,
          type,
        };
      }) || [];
  if (tasks.length > 0) {
    updatedRecipe.tasks.task = tasks;
  } else {
    delete updatedRecipe.tasks;
  }

  // Add new section for imports after the initial setup
  const imports =
    modifiedNodes
      ?.filter((node) => AllowedImportTypes?.includes(node?.type))
      ?.map((node) => {
        const existingImport = updatedRecipe?.imports?.import?.find(
          (imp) => imp?.['@id'] === node?.id
        );
        return {
          ...(existingImport || {}),
          ...node?.data,
          '@id': node?.id,
        };
      }) || [];
  if (imports.length > 0) {
    updatedRecipe.imports.import = imports;
  } else {
    delete updatedRecipe.imports;
  }

  // Process edges to update references
  if (updatedRecipe.statements?.statement) {
    updatedRecipe.statements.statement.forEach((statement) => {
      // Initialize arrays for different types of references
      const connectorIds = new Set();
      const dataframeIds = new Set();
      const readFormatIds = new Set();
      const writeFormatIds = new Set();
      const taskIds = new Set();

      // Find edges with this statement as the target
      modifiedEdges?.forEach((edge) => {
        if (edge?.target === statement?.['@id']) {
          const sourceNode = modifiedNodes?.find((node) => node?.id === edge?.source);

          if (sourceNode) {
            if (
              AllowedConnectorTypes?.includes(sourceNode?.type) &&
              sourceNode?.type !== 'localFileImporter'
            ) {
              connectorIds?.add(sourceNode?.id);
            } else if (AllowedStatementTypes?.includes(sourceNode?.type)) {
              dataframeIds?.add(sourceNode?.id);
            } else if (AllowedReadFormatTypes?.includes(sourceNode?.type)) {
              readFormatIds?.add(sourceNode?.id);
            } else if (AllowedWriteFormatTypes?.includes(sourceNode?.type)) {
              writeFormatIds?.add(sourceNode?.id);
            } else if (AllowedTaskTypes?.includes(sourceNode?.type)) {
              taskIds?.add(sourceNode?.id);
            } else if (AllowedImportTypes?.includes(sourceNode?.type)) {
              // Handle import references if needed in the future
            }
          }
        }
      });

      // Update references by joining unique IDs
      if (connectorIds?.size > 0) {
        statement['@ref-connector-id'] = Array.from(connectorIds)?.join(',');
      }
      if (dataframeIds?.size > 0) {
        statement['@ref-dataframe-id'] = Array.from(dataframeIds)?.join(',');
      }
      if (readFormatIds?.size > 0) {
        statement['@ref-readFormat-id'] = Array.from(readFormatIds)?.join(',');
      }
      if (writeFormatIds?.size > 0) {
        statement['@ref-writeFormat-id'] = Array.from(writeFormatIds)?.join(',');
      }
      if (taskIds?.size > 0) {
        statement['@ref-task-id'] = Array.from(taskIds)?.join(',');
      }

      // Delete the id property
      delete statement?.id;
    });
  }

  // Process edges to update connector references
  if (updatedRecipe.connectors?.connector) {
    updatedRecipe.connectors.connector.forEach((connector) => {
      // Initialize arrays for different types of references
      const statementIds = new Set();
      const readFormatIds = new Set();
      const taskIds = new Set();

      // Find edges with this connector as the target
      modifiedEdges?.forEach((edge) => {
        if (edge?.target === connector?.['@id']) {
          const sourceNode = modifiedNodes?.find((node) => node?.id === edge?.source);

          if (sourceNode) {
            if (AllowedStatementTypes?.includes(sourceNode?.type)) {
              statementIds?.add(sourceNode?.id);
            } else if (AllowedReadFormatTypes?.includes(sourceNode?.type)) {
              readFormatIds?.add(sourceNode?.id);
            } else if (AllowedTaskTypes?.includes(sourceNode?.type)) {
              taskIds?.add(sourceNode?.id);
            }
          }
        }
      });

      // Update references by joining unique IDs
      if (statementIds?.size > 0) {
        connector['@ref-statement-id'] = Array.from(statementIds)?.join(',');
      }
      if (readFormatIds?.size > 0) {
        connector['@ref-readFormat-id'] = Array.from(readFormatIds)?.join(',');
      }
      if (taskIds?.size > 0) {
        connector['@ref-task-id'] = Array.from(taskIds)?.join(',');
      }

      // Delete the id property
      delete connector?.id;
    });
  }

  // Process edges to update task references
  if (updatedRecipe.tasks?.task) {
    updatedRecipe.tasks.task.forEach((task) => {
      // Initialize arrays for different types of references
      const statementIds = new Set();
      const connectorIds = new Set();
      const readFormatIds = new Set();
      const writeFormatIds = new Set();
      // Find edges with this task as the target
      modifiedEdges?.forEach((edge) => {
        if (edge?.target === task?.['@id']) {
          const sourceNode = modifiedNodes?.find((node) => node?.id === edge?.source);

          if (sourceNode) {
            if (AllowedStatementTypes?.includes(sourceNode?.type)) {
              statementIds?.add(sourceNode?.id);
            } else if (
              AllowedConnectorTypes?.includes(sourceNode?.type) &&
              sourceNode?.type !== 'localFileImporter'
            ) {
              connectorIds?.add(sourceNode?.id);
            } else if (AllowedReadFormatTypes?.includes(sourceNode?.type)) {
              readFormatIds?.add(sourceNode?.id);
            } else if (AllowedWriteFormatTypes?.includes(sourceNode?.type)) {
              writeFormatIds?.add(sourceNode?.id);
            }
          }
        }
      });

      // Update references by joining unique IDs
      if (statementIds?.size > 0) {
        task['@ref-statement-id'] = Array.from(statementIds)?.join(',');
      }
      if (connectorIds?.size > 0) {
        task['@ref-connector-id'] = Array.from(connectorIds)?.join(',');
      }
      if (readFormatIds?.size > 0) {
        task['@ref-readFormat-id'] = Array.from(readFormatIds)?.join(',');
      }
      if (writeFormatIds?.size > 0) {
        task['@ref-writeFormat-id'] = Array.from(writeFormatIds)?.join(',');
      }

      // Delete the id property
      delete task?.id;
    });
  }

  // Process edges to update readFormat references
  if (updatedRecipe.readFormats?.readFormat) {
    updatedRecipe.readFormats.readFormat.forEach((readFormat) => {
      // Initialize arrays for different types of references
      const statementIds = new Set();
      const connectorIds = new Set();
      const taskIds = new Set();

      // Find edges with this readFormat as the target
      modifiedEdges?.forEach((edge) => {
        if (edge?.target === readFormat?.['@id']) {
          const sourceNode = modifiedNodes?.find((node) => node?.id === edge?.source);

          if (sourceNode) {
            if (AllowedStatementTypes?.includes(sourceNode?.type)) {
              statementIds?.add(sourceNode?.id);
            } else if (
              AllowedConnectorTypes?.includes(sourceNode?.type) &&
              sourceNode?.type !== 'localFileImporter'
            ) {
              connectorIds?.add(sourceNode?.id);
            } else if (AllowedTaskTypes?.includes(sourceNode?.type)) {
              taskIds?.add(sourceNode?.id);
            }
          }
        }
      });

      // Update references by joining unique IDs
      if (statementIds?.size > 0) {
        readFormat['@ref-statement-id'] = Array.from(statementIds)?.join(',');
      }
      if (connectorIds?.size > 0) {
        readFormat['@ref-connector-id'] = Array.from(connectorIds)?.join(',');
      }
      if (taskIds?.size > 0) {
        readFormat['@ref-task-id'] = Array.from(taskIds)?.join(',');
      }

      // Delete the id property
      delete readFormat?.id;
    });
  }

  // Process edges to update writeFormat references
  if (updatedRecipe.writeFormats?.writeFormat) {
    updatedRecipe.writeFormats.writeFormat.forEach((writeFormat) => {
      // Initialize arrays for different types of references
      const statementIds = new Set();
      const connectorIds = new Set();
      const taskIds = new Set();

      // Find edges with this writeFormat as the target
      modifiedEdges?.forEach((edge) => {
        if (edge?.target === writeFormat?.['@id']) {
          const sourceNode = modifiedNodes?.find((node) => node?.id === edge?.source);

          if (sourceNode) {
            if (AllowedStatementTypes?.includes(sourceNode?.type)) {
              statementIds?.add(sourceNode?.id);
            } else if (
              AllowedConnectorTypes?.includes(sourceNode?.type) &&
              sourceNode?.type !== 'localFileImporter'
            ) {
              connectorIds?.add(sourceNode?.id);
            } else if (AllowedTaskTypes?.includes(sourceNode?.type)) {
              taskIds?.add(sourceNode?.id);
            }
          }
        }
      });

      // Update references by joining unique IDs
      if (statementIds?.size > 0) {
        writeFormat['@ref-statement-id'] = Array.from(statementIds)?.join(',');
      }
      if (connectorIds?.size > 0) {
        writeFormat['@ref-connector-id'] = Array.from(connectorIds)?.join(',');
      }
      if (taskIds?.size > 0) {
        writeFormat['@ref-task-id'] = Array.from(taskIds)?.join(',');
      }

      // Delete the id property
      delete writeFormat?.id;
    });
  }

  // Add new section for processing import references (before the return statement)
  if (updatedRecipe.imports?.import) {
    updatedRecipe.imports.import.forEach((importItem) => {
      // Initialize arrays for different types of references
      const statementIds = new Set();
      const connectorIds = new Set();
      const taskIds = new Set();

      // Find edges with this import as the target
      modifiedEdges?.forEach((edge) => {
        if (edge?.target === importItem?.['@id']) {
          const sourceNode = modifiedNodes?.find((node) => node?.id === edge?.source);

          if (sourceNode) {
            if (AllowedStatementTypes?.includes(sourceNode?.type)) {
              statementIds?.add(sourceNode?.id);
            } else if (
              AllowedConnectorTypes?.includes(sourceNode?.type) &&
              sourceNode?.type !== 'localFileImporter'
            ) {
              connectorIds?.add(sourceNode?.id);
            } else if (AllowedTaskTypes?.includes(sourceNode?.type)) {
              taskIds?.add(sourceNode?.id);
            }
          }
        }
      });

      // Update references by joining unique IDs
      if (statementIds?.size > 0) {
        importItem['@ref-statement-id'] = Array.from(statementIds)?.join(',');
      }
      if (connectorIds?.size > 0) {
        importItem['@ref-connector-id'] = Array.from(connectorIds)?.join(',');
      }
      if (taskIds?.size > 0) {
        importItem['@ref-task-id'] = Array.from(taskIds)?.join(',');
      }

      // Delete the id property
      delete importItem?.id;
    });
  }

  // run pre save transformers
  const res = preSaveTransformers({
    recipe: updatedRecipe,
    nodes: modifiedNodes,
    edges: modifiedEdges,
  });

  if (res?.isError) {
    logger.log('Errors', res?.errors);
  }

  // Ensure we return a valid recipe even if it's empty
  const finalRecipe = res?.recipe || updatedRecipe;

  // If recipe is completely empty, return a minimal valid structure
  if (Object.keys(finalRecipe).length === 0) {
    return {
      '@id': originalRecipe?.['@id'] || 'root-recipe-1',
      '@data-comment': originalRecipe?.['@data-comment'] || '',
      connectors: { connector: [] },
      statements: { statement: [] },
    };
  }

  return finalRecipe;
};
